// Quick Start (DESIGN §7): newGame({ startYear: 1901 }) builds a "modest" M1 company directly on 2 Jan 1901 —
// no 41-year tick-through. Deterministic by seed (main rng only), JSON-safe.
//   cash ≈ $12k (M2_SPEC §17 "modest" tier: one Spindletop lot + contract drilling, or the own rotary outfit on hand),
//   a steel wire-line cable rig with a pioneer crew, an early rotary with a rotary crew, pump, pipe, mud and bits,
//   two producing Oil Creek wells on owned leases with a tank and the trunk line, M1 techs known, flags and price
//   history consistent with the date.
import { dateToDay } from '../core/time.js';
import { NEWS, applyNewsEffect } from '../data/news.js';
import { TECH } from '../data/tech.js';
import { initMarket, basePriceForDay } from '../economy/market.js';
import { addRig, addItem, addStock } from '../core/state.js';
import { eqDef, fieldDef } from './defs.js';
import { reservoirsAt } from './geo.js';
import { makeCrew, refreshHirePool } from './crew.js';
import { newMonth } from './finance.js';
import { createWell } from './drilling.js';
import { startProduction } from './production.js';
import { initRefining } from './refining.js';
import { leaseRoyalty } from './survey.js';
// M3 Quick Starts (1946, 1973)
import { dayToDate } from '../core/time.js';
import { QUICKSTART_1946, QUICKSTART_1973, EOR_METHODS, eorRfAdd } from '../data/m3/rules.js';
import { nextId } from './util.js';
import { fieldTransports } from './defs.js';
import { stampWell } from './m3/offshore.js';
import { platformQuote } from './m3/platforms.js';

export const QUICKSTART_1901 = { day: dateToDay(1901, 1, 2), cash: 12000 };

function crewUp(state, rig, roles, skill = 55) {
  for (const [role, n] of Object.entries(roles)) for (let i = 0; i < n; i++) {
    const c = makeCrew(state, role, skill);
    c.hiredDay = state.day - 200; c.rigId = rig.uid;
    state.crew[c.id] = c;
    rig.crewIds.push(c.id);
  }
}

function installed(state, rig, defId, slot) {
  const it = addItem(state, defId, { type: 'rig', id: rig.uid });
  if (slot === 'engine') rig.engineUid = it.uid;
  else if (slot === 'bit') rig.bitUid = it.uid;
  else rig.gear[slot] = it.uid;
  return it;
}

export function quickStart1901(state) {
  const { day, cash } = QUICKSTART_1901;
  // wipe the 1859 starter kit
  state.rigs = {}; state.items = {}; state.stock = {}; state.crew = {}; state.wells = {};
  for (const f of Object.values(state.fields)) for (const p of f.plots) { p.owned = false; p.wellId = null; p.leaseDay = null; p.leaseExpires = null; }
  state.day = day;
  state.eraId = 'gusher';
  state.flags.eraSummaryShown = { pioneer: dateToDay(1859, 8, 1), gusher: day };
  state.company.cash = cash;
  state.company.reputation = 45;
  state.company.accidentFreeSince = day - 365;
  state.company.founded = dateToDay(1859, 8, 1);
  state.finance = { current: newMonth(day), history: [] };
  // news & flags up to today (no events fire: newsForDay is keyed by exact day)
  for (const n of NEWS) if (n.day <= day) applyNewsEffect(state, n);
  state.news = NEWS.filter((n) => n.day <= day && n.headline).slice(-12).map((n) => ({ id: n.id, day: n.day, text: n.headline, kind: n.kind, fieldId: n.fieldId ?? null, regionId: n.regionId ?? null }));
  // market + a monthly price history since 1859 (deterministic base prices)
  initMarket(state);
  const hist = [];
  for (let d = dateToDay(1859, 8, 1); d < day; d += 30) hist.push([d, Math.round(basePriceForDay(d) * 100) / 100]);
  state.market.history = [...hist, ...state.market.history];
  delete state.productMarkets;
  initRefining(state);
  // research: every Pioneer technology is common knowledge by 1901
  for (const t of Object.values(TECH)) if (t.historicalYear <= 1900) state.research.done[t.id] = { day, mode: 'leaked', patentExpires: null, inventedYear: t.historicalYear, patentable: false };

  // cable-tool outfit
  const cable = addRig(state, 'steel_wireline', 'Rig 1');
  cable.tours = 2;
  installed(state, cable, 'steam_20hp', 'engine');
  installed(state, cable, 'star_bit', 'bit');
  cable.line = { defId: 'steel_wire_line', ft: 2500, condition: 90 };
  crewUp(state, cable, { driller: 1, tool_dresser: 1, laborer: 1 });
  addItem(state, 'star_bit');
  // early rotary outfit
  const rot = addRig(state, 'early_rotary', 'Rig 2');
  rot.tours = 2;
  installed(state, rot, 'steam_20hp', 'engine');
  installed(state, rot, 'fishtail_bit', 'bit');
  installed(state, rot, 'duplex_steam_pump', 'pump');
  crewUp(state, rot, { driller: 1, derrickman: 1, roughneck: 2, motorman: 1 });
  for (let i = 0; i < 7; i++) addItem(state, 'fishtail_bit');   // cap rock eats fishtails
  addStock(state, 'lapweld_pipe', 40);
  addStock(state, 'native_clay', 1200);
  addStock(state, 'steel_casing', 200);
  addStock(state, 'wrought_iron_casing', 100);

  // two producing Oil Creek wells (crest of prospect A), a tank and the trunk line
  const fid = 'oil_creek';
  const f = state.fields[fid];
  const fdef = fieldDef(fid);
  const plots = [fdef.startPlot ?? 7, ...f.plots.map((p, i) => i).filter((i) => i !== (fdef.startPlot ?? 7) && reservoirsAt(fdef, f.plots[i]).length)].slice(0, 3);
  let n = 0;
  for (const idx of plots) {
    const res = reservoirsAt(fdef, f.plots[idx]);
    if (!res.length || n >= 2) continue;
    const r = res[res.length > 1 ? 1 : 0];
    const p = f.plots[idx];
    p.owned = true; p.leaseDay = day - 3000; p.leaseExpires = null; p.royalty = leaseRoyalty(state, fid);
    const w = createWell(state, cable, fid, idx, r.top + r.thick);
    w.rigId = null; w.bitUid = null; w.spudDay = day - 2900 + n * 400;
    w.depth = r.top + r.thick; w.payTop = r.top; w.payFt = r.thick; w.reservoirId = r.id; w.revealed = [r.id];
    w.strikeDay = w.spudDay + 20; w.strikeRigCond = 90;
    w.casing = [{ defId: 'wrought_iron_casing', top: 0, bottom: Math.min(r.top - 5, 300), cemented: false, day: w.spudDay + 10 }];
    w.shoe = w.casing[0].bottom;
    w.status = 'AT_TD';
    startProduction(state, w);
    w.prod.age = 365 * 4 + n * 200;
    w.prod.startDay = w.spudDay + 25;
    state.company.stats.wellsDrilled++;
    state.company.stats.strikes++;
    n++;
  }
  addItem(state, 'iron_tank', { type: 'field', id: fid });
  if (eqDef('trunk_pipeline')) { f.connected.push('trunk_pipeline'); f.transportId = 'trunk_pipeline'; }
  state.company.stats.ftDrilled = 4200;
  state.log = [];
  refreshHirePool(state);
  state._poolDay = day;
  seedInherited(state);
  return state;
}

// polish (G7): Quick Start wells are inherited — flag them (achievements skip them) and show a sensible rate before the
// first tick (Arps decline at the well's age; tickProduction overwrites it on day 1). Display-only, no RNG.
function seedInherited(state) {
  for (const w of Object.values(state.wells)) {
    w.inherited = true;
    const p = w.prod;
    if (p && !p.rate && p.qi > 0) {
      const t = (p.age ?? 0) / 365, b = p.b || 0.5, di = p.di || 0.6;
      p.rate = Math.round((p.qi / Math.pow(1 + b * di * t, 1 / b)) * 10) / 10;
    }
  }
}

// ---------------------------------------------------------------------------------------------------------------------
// M3 Quick Starts (M3_SPEC §17.2): newGame({ startYear: 1946 | 1973 }) builds the company directly on the day from
// rules.QUICKSTART_1946 / QUICKSTART_1973 — no 87-year tick-through. Deterministic by seed, JSON-safe.
export { QUICKSTART_1946, QUICKSTART_1973 };

function crestPlots(state, fid, { prospect = null, near = null, need = 1 } = {}) {
  const f = state.fields[fid];
  const fdef = fieldDef(fid);
  const gw = fdef.grid?.w ?? 6;
  return f.plots.map((p, i) => ({ i, p, res: reservoirsAt(fdef, p) }))
    .filter((x) => x.res.length && !x.p.owned && !x.p.wellId && (prospect == null || x.p.truth.prospectIdx === prospect)
      && (near == null || Math.max(Math.abs((x.i % gw) - (near % gw)), Math.abs(Math.floor(x.i / gw) - Math.floor(near / gw))) <= 1))
    .sort((a, b) => (a.p.truth.edge ?? 1) - (b.p.truth.edge ?? 1) || a.i - b.i).slice(0, need);
}

function producer(state, rig, fid, idx, ageDays, { platformUid = null } = {}) {
  const f = state.fields[fid];
  const fdef = fieldDef(fid);
  const day = state.day;
  const p = f.plots[idx];
  const res = reservoirsAt(fdef, p);
  const r = res[0];
  p.owned = true; p.leaseDay = day - ageDays - 120; p.leaseExpires = null; p.royalty = leaseRoyalty(state, fid);
  const w = createWell(state, rig, fid, idx, r.top + r.thick);
  w.rigId = null; w.bitUid = null; w.rigType = 'rotary';
  w.spudDay = day - ageDays - 60;
  w.depth = r.top + Math.min(r.thick, 60); w.payTop = r.top; w.payFt = Math.min(r.thick, 60); w.reservoirId = r.id; w.revealed = [r.id];
  w.strikeDay = w.spudDay + 40; w.strikeRigCond = 90;
  w.casing = [{ defId: 'api_casing', top: 0, bottom: Math.round(r.top - 10), cemented: 'portland_cement', day: w.spudDay + 30 }];
  w.shoe = w.casing[0].bottom;
  w.status = 'AT_TD';
  if (platformUid) { w.platformUid = platformUid; state.platforms[platformUid].slotsUsed.push(w.id); }
  stampWell(state, w);
  startProduction(state, w);
  w.prod.age = ageDays;
  w.prod.startDay = day - ageDays;
  w.firstProdYear = dayToDate(Math.max(0, day - ageDays)).year;
  state.company.stats.wellsDrilled++;
  state.company.stats.strikes++;
  state.company.stats.ftDrilled += w.depth;
  return w;
}

function m3Rig(state, spec) {
  const rig = addRig(state, spec.defId, spec.name);
  rig.tours = spec.tours ?? 2;
  rig.builtYear = spec.builtYear ?? Math.min(dayToDate(state.day).year, eqDef(spec.defId)?.year ?? 1946);
  rig.offshore = !!eqDef(spec.defId)?.stats?.offshore;
  if (spec.engine) installed(state, rig, spec.engine, 'engine');
  if (spec.bit) { installed(state, rig, spec.bit, 'bit'); for (let i = 0; i < 2; i++) addItem(state, spec.bit); }
  for (const [slot, id] of [['pump', spec.pump], ['collars', spec.collars]]) if (id) installed(state, rig, id, slot);
  if (spec.bop) rig.bopUid = addItem(state, spec.bop, { type: 'rig', id: rig.uid }).uid;
  for (const g of spec.gear ?? []) { const d = eqDef(g); if (d) installed(state, rig, g, d.stats?.slot ?? g); }
  crewUp(state, rig, spec.crew ?? {}, 58);
  return rig;
}

export function quickStartM3(state, Q, startYear) {
  const day = Q.day;
  state.rigs = {}; state.items = {}; state.stock = {}; state.crew = {}; state.wells = {};
  for (const f of Object.values(state.fields)) for (const p of f.plots) { p.owned = false; p.wellId = null; p.leaseDay = null; p.leaseExpires = null; }
  state.day = day;
  state.eraId = Q.eraId;
  state.flags.eraSummaryShown = { pioneer: dateToDay(1859, 8, 1), gusher: dateToDay(1901, 1, 1), postwar: startYear >= 1973 ? dateToDay(1946, 1, 1) : day, ...(startYear >= 1973 ? { offshore: day } : {}) };
  state.company.cash = Q.cash;
  state.company.reputation = Q.reputation;
  state.company.accidentFreeSince = day - 365;
  state.company.founded = dateToDay(1859, 8, 1);
  state.finance = { current: newMonth(day), history: [] };
  for (const n of NEWS) if (n.day <= day) applyNewsEffect(state, n);
  state.news = NEWS.filter((n) => n.day <= day && n.headline).slice(-12).map((n) => ({ id: n.id, day: n.day, text: n.headline, kind: n.kind, fieldId: n.fieldId ?? null, regionId: n.regionId ?? null }));
  initMarket(state);
  const hist = [];
  for (let d = dateToDay(1859, 8, 1); d < day; d += 30) hist.push([d, Math.round(basePriceForDay(d) * 100) / 100]);
  state.market.history = [...hist, ...state.market.history];
  delete state.productMarkets;
  initRefining(state);
  const known = dayToDate(day).year - 1;
  for (const t of Object.values(TECH)) if (t.historicalYear <= known) state.research.done[t.id] = { day, mode: 'leaked', patentExpires: null, inventedYear: t.historicalYear, patentable: false };

  const rigs = Q.rigs.map((spec) => m3Rig(state, spec));
  const land = rigs.find((r) => !r.offshore) ?? rigs[0];
  for (const [role, n] of Object.entries(Q.extraCrew ?? {})) for (let i = 0; i < n; i++) { const c = makeCrew(state, role, 55); c.hiredDay = day - 200; if (role === 'toolpusher') c.pushes = []; state.crew[c.id] = c; }
  for (const [id, q] of Object.entries(Q.stock ?? {})) { const d = eqDef(id); if (!d) continue; if (d.cat === 'bit') for (let i = 0; i < q; i++) addItem(state, id); else addStock(state, id, q); }
  for (const id of Q.labs ?? []) if (eqDef(id)) state.research.labs.push(addItem(state, id, { type: 'hq', id: null }).uid);

  // offshore structures first (wells register their slots)
  const pfByField = {};
  for (const ps of Q.platforms ?? []) {
    const fdef = fieldDef(ps.fieldId);
    const pr = fdef.prospects[1] ?? fdef.prospects[0];
    const gw = fdef.grid.w;
    const idx = Math.round(pr.cy) * gw + Math.round(pr.cx);
    const q = platformQuote(state, ps.fieldId, idx, ps.defId);
    const uid = nextId(state, 'pf');
    (state.platforms ??= {})[uid] = { uid, defId: ps.defId, fieldId: ps.fieldId, plot: idx, status: 'INSTALLED', timerId: null, price: Math.round(q.price), orderedDay: day - ps.ageDays - 540, builtDay: day - ps.ageDays,
      waterDepthFt: q.water, slotsUsed: [], rigUids: [], hostOf: [], condition: 90, exportOk: true };
    state.fields[ps.fieldId].plots[idx].owned = true;
    pfByField[ps.fieldId] = { uid, idx, n: ps.wells, ageDays: ps.ageDays };
  }
  const made = {};
  for (const ws of Q.wells) {
    const pf = pfByField[ws.fieldId];
    const plots = pf ? crestPlots(state, ws.fieldId, { near: pf.idx, need: ws.count + 1 }) : crestPlots(state, ws.fieldId, { need: ws.count });
    if (pf && !plots.some((x) => x.i === pf.idx)) state.fields[ws.fieldId].plots[pf.idx].owned = false;
    if (pf) for (const x of plots) if (x.i === pf.idx) state.fields[ws.fieldId].plots[pf.idx].owned = false;
    const list = (made[ws.fieldId] ??= []);
    for (const x of plots.slice(0, ws.count)) list.push(producer(state, land, ws.fieldId, x.i, ws.ageDays, { platformUid: pf?.uid ?? null }));
    if (ws.waterflood && list.length >= 2) {
      const resId = list[0].reservoirId;
      const m = EOR_METHODS.waterflood;
      const rs = state.fields[ws.fieldId].reservoirs[resId];
      const rdef = reservoirsAt(fieldDef(ws.fieldId), state.fields[ws.fieldId].plots[list[0].plot]).find((r) => r.id === resId);
      const inj = list[list.length - 1];
      inj.injector = true; inj.prod.shutIn = true; inj.prod.rate = 0;
      (state.m3 ??= { charterBoard: [], boardDay: 0, eor: {}, megaprojects: {}, supply: {}, crewChange: {}, stormsSeen: {}, nextStorm: 1 }).eor[`${ws.fieldId}:${resId}`] = {
        fieldId: ws.fieldId, resId, method: 'waterflood', startDay: day - m.lagDays - m.rampDays - 30, lagDays: m.lagDays, rampDays: m.rampDays,
        rfAdd: eorRfAdd(rdef, 'waterflood'), ooip: Math.round(rs.initial / (rdef.rf ?? 0.25)), applied: 0, injectorId: inj.id, cost: 0 };
      state.company.stats.eorProjects = (state.company.stats.eorProjects ?? 0) + 1;
    }
  }
  if (Q === QUICKSTART_1946) {
    // Signal Hill's wells are a decade older; Permian is fresh: nothing else to do. A Texas tank farm.
  }
  for (const st of Q.storage ?? []) if (eqDef(st.defId)) addItem(state, st.defId, { type: 'field', id: st.fieldId });
  for (const fid of Object.keys(made)) if (!pfByField[fid] && !(Q.storage ?? []).some((x) => x.fieldId === fid)) addItem(state, 'steel_tank_55k', { type: 'field', id: fid }); // every onshore lease has a tank battery
  for (const [fid, tid] of Object.entries(Q.transport ?? {})) { const f = state.fields[fid]; if (f && eqDef(tid)) { if (!f.connected.includes(tid)) f.connected.push(tid); f.transportId = tid; } }
  for (const fid of Object.keys(made)) {
    const f = state.fields[fid];
    if (!f.transportId || !fieldTransportOk(fid, f.transportId, day)) {
      const t = fieldTransports(fieldDef(fid)).find((x) => x.year <= dayToDate(day).year && eqDef(x.id) && !String(eqDef(x.id).stats?.requires ?? '').startsWith('megaproject'));
      if (t) { if (!f.connected.includes(t.id)) f.connected.push(t.id); f.transportId = t.id; }
    }
    if (pfByField[fid]) { const t = fieldTransports(fieldDef(fid)).find((x) => /pipeline/.test(x.id) && x.year <= dayToDate(day).year); if (t) { if (!f.connected.includes(t.id)) f.connected.push(t.id); f.transportId = t.id; } }
  }
  // the offshore rig parks at its field; logistics contracted to it
  const off = rigs.find((r) => r.offshore);
  if (off) {
    const fid = Q.platforms?.[0]?.fieldId ?? 'gulf_shelf';
    const pf = pfByField[fid];
    off.location = { fieldId: fid, plot: pf?.idx ?? 0 };
    off.at = { fieldId: fid, plot: pf?.idx ?? 0 };
    for (const id of Q.logistics ?? []) if (eqDef(id)) { const uid = nextId(state, 'lg'); (state.logistics ??= {})[uid] = { uid, defId: id, assignedTo: { kind: 'rig', id: off.uid }, startDay: day }; }
  }
  for (const l of Q.licences ?? []) {
    for (const x of crestPlots(state, l.fieldId, { need: l.plots ?? 1 })) {
      x.p.owned = true; x.p.leaseDay = day - 2000; x.p.leaseExpires = null; x.p.royalty = fieldDef(l.fieldId).royalty ?? 0.125;
      x.p.licence = { share: 0.1, operator: 'BP', roundId: fieldDef(l.fieldId).concession?.bidRound ?? null, note: l.note ?? null };
    }
  }
  state.log = [];
  refreshHirePool(state);
  state._poolDay = day;
  state.startYear = startYear;
  seedInherited(state);
  return state;
}

function fieldTransportOk(fid, tid, day) {
  const t = fieldTransports(fieldDef(fid)).find((x) => x.id === tid);
  return !!t && t.year <= dayToDate(day).year;
}

export const quickStart1946 = (state) => quickStartM3(state, QUICKSTART_1946, 1946);
export const quickStart1973 = (state) => quickStartM3(state, QUICKSTART_1973, 1973);
